"use client";

import { getRandomProductsByCollection } from "@/services/api/collections";
import ProductCard from "@/features/cake-customization/ProductCard";
import { Product } from "@/types/product";
import { useQuery } from "@tanstack/react-query";

const COLLECTION_ID = "47";

const YouMayAlsoLike = ({
  productSlug,
  vendorId,
}: {
  productSlug: string;
  vendorId?: number;
}) => {
  const { data } = useQuery({
    queryKey: ["you-may-also-like", COLLECTION_ID, vendorId],
    queryFn: () => getRandomProductsByCollection(COLLECTION_ID, vendorId),
    select: (products) => {
      if (!Array.isArray(products)) return [];

      return products
        .filter(
          (product: Product) =>
            product.slug !== productSlug && product.in_stock !== false,
        )
        .slice(0, 12);
    },
  });

  if (!data || data.length === 0) return null;

  return (
    <section className="w-full py-8 lg:py-16 px-4 lg:px-12 bg-[#FAF7F2] text-[#1C1C1C] overflow-hidden relative">
      {/* Subtle border to frame the section */}

      <div className="max-w-[1440px] mx-auto flex flex-col gap-8 md:gap-10 relative z-10">
        {/* Editorial Header */}
        <div className="flex flex-col md:flex-row justify-between items-center md:items-end border-b border-[#E5E0D8] pb-6 md:pb-8 text-center md:text-left">
          <div className="max-w-xl flex flex-col items-center md:items-start w-full">
            <span className="uppercase tracking-[0.15em] md:tracking-[0.2em] text-[#9A8471] text-[10px] md:text-xs font-semibold mb-3 md:mb-4 flex items-center justify-center md:justify-start gap-3 md:gap-4 w-full">
              <span className="w-8 md:w-12 h-[1px] bg-[#9A8471]"></span>
              Curated For You
              <span className="w-8 h-[1px] bg-[#9A8471] md:hidden"></span>
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight text-[#1C1C1C]">
              You May Also <span className="italic text-[#9A8471]">Love</span>
            </h2>
          </div>
        </div>

        {/* Dynamic Grid */}
        <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 pt-4">
          {data.slice(0, 9).map((product: Product) => {
            if (!product) return null;
            return (
              <div
                key={product.slug}
                className="group relative transition-transform duration-500 hover:-translate-y-2"
              >
                {/* Clean Product Card without heavy dark-mode shadows */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-[#EBE7E0]">
                  <ProductCard product={product} productPage={true} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="lg:hidden grid grid-cols-2 gap-6 lg:gap-8 pt-4">
          {data.map((product: Product) => {
            if (!product) return null;
            return (
              <div
                key={product.slug}
                className="group relative transition-transform duration-500 hover:-translate-y-2"
              >
                {/* Clean Product Card without heavy dark-mode shadows */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-[#EBE7E0]">
                  <ProductCard product={product} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default YouMayAlsoLike;
